
import { Component, Ref, Vue } from 'vue-property-decorator'
import StudentLearningProgressLayout from '@/components/templates/manage/StudentLearningProgressLayout.vue'
import { GradeCode } from '@/const/GradeCode'
import { SubjectCode } from '@/const/SubjectCode'
import { SelectItem } from '@/models/SelectItem'
import { StudentProgress } from '@/models/manage/StudentProgress'
import { AcademyCoursesApi } from '@/api/methods/manage/AcademyCoursesApi'
import { AcademyClassesApi } from '@/api/methods/manage/AcademyClassesApi'
import { UserStudentsApi } from '@/api/methods/manage/UserStudentsApi'
import { SystemChartsApi } from '@/api/methods/SystemChartsApi'
import StringUtils from '@/utils/StringUtils'

/**
 * 教室生徒の知識MAP系統図進捗確認画面
 */
@Component({
  components: {
    StudentLearningProgressLayout,
  },
})
export default class BranchStudentsSystemChartProgress extends Vue {
  /** 生徒一覧 */
  @Ref()
  studentLearningProgressLayout!: StudentLearningProgressLayout

  /** 1ページの最大件数 */
  private readonly MAX_PER_PAGE = 7

  /** ページ指定の初期値 */
  private readonly DEFAULT_PAGE_NUMBER = 1

  /** プルダウン選択固定値:全て */
  private readonly OPTION_ALL = { value: 'all', label: '全て' }

  /** プルダウン選択固定値:選択不可 */
  private readonly OPTION_DISABLED = { value: 'disabled', label: '選択不可' }

  /** 生徒一覧 */
  private studentList: StudentProgress[] = []

  /** 生徒人数 */
  private studentsNumber = 0

  /** コースフィルターの選択肢 */
  private filterAcademyCourseList: SelectItem[] = []

  /** クラスフィルターの選択肢 */
  private filterAcademyClassList: SelectItem[] = []

  /** クラスフィルターのdisabled制御 */
  private isClassFilterDisabled = false

  /** 系統図フィルターの選択肢 */
  private filterSystemChartList: SelectItem[] = []

  /** ページ数 */
  private pageLength = 0

  /** 現在のページ */
  private currentPage = this.DEFAULT_PAGE_NUMBER

  /** 先生情報 */
  private userBranch = this.$appCookies.authTeacher.get()

  /**
   * 学年フィルターの初期選択値
   *
   * @returns 選択肢「全て」のコード
   */
  private get defaultGardeCode(): string {
    let gradeCode = this.$route.query.gradeCode

    if (!gradeCode || typeof gradeCode !== 'string') {
      // 未指定時、指定不正時は空文字列に置換
      gradeCode = ''
    }

    // 定義情報から有効な学年情報を取得
    const grade = GradeCode.findByCode(gradeCode)

    return grade?.code || GradeCode.LIST[0].code
  }

  /**
   * 学年フィルターの選択肢
   *
   * @returns 学年リスト
   */
  private get filterGradeList(): SelectItem[] {
    return GradeCode.LIST.map((grade) => {
      return {
        value: grade.code,
        label: grade.label,
      }
    })
  }

  /**
   * 教科フィルターの初期選択値
   *
   * @returns 教科リストの1番目のコード
   */
  private get defaultSubjectCode(): string {
    let subjectCode = this.$route.query.subjectCode

    if (!subjectCode || typeof subjectCode !== 'string') {
      // 未指定時、指定不正時は空文字列に置換
      subjectCode = ''
    }

    // 定義情報から有効な学年情報を取得
    const subject = SubjectCode.findByCode(subjectCode)

    return subject?.code || SubjectCode.LIST[0].code
  }

  /**
   * 教科フィルターの選択肢
   *
   * @returns 教科リスト
   */
  private get filterSubjectList(): SelectItem[] {
    return SubjectCode.LIST.map((subject) => {
      return {
        value: subject.code,
        label: subject.label,
      }
    })
  }

  /**
   * クエリパラメータから取得したコースID
   *
   * @returns コースID
   */
  private get queryCourseId(): string | undefined {
    const courseId = this.$route.query.courseId

    if (!courseId || typeof courseId !== 'string') {
      // 未指定時、string型でない場合はundefinedを返す
      return undefined
    }

    return courseId
  }

  /**
   * クエリパラメータから取得したクラスID
   *
   * @returns クラスID
   */
  private get queryClassId(): string | undefined {
    const classId = this.$route.query.classId

    if (!classId || typeof classId !== 'string') {
      // 未指定時、string型でない場合はundefinedを返す
      return undefined
    }

    return classId
  }

  /**
   * クエリパラメータから取得した検索文字列
   *
   * @returns 検索文字列
   */
  private get querySearchText(): string | undefined {
    const searchText = this.$route.query.searchText

    if (!searchText || typeof searchText !== 'string') {
      // 未指定時、string型でない場合はundefinedを返す
      return undefined
    }

    return searchText
  }

  /**
   * クエリパラメータから取得した系統図ID
   *
   * @returns 系統図ID
   */
  private get querySystemChartId(): string | undefined {
    const systemChartId = this.$route.query.systemChartId

    if (!systemChartId || typeof systemChartId !== 'string') {
      // 未指定時、string型でない場合はundefinedを返す
      return undefined
    }

    return systemChartId
  }

  /**
   * クエリパラメータから取得したページ番号
   *
   * @returns ページ番号
   */
  private get queryPageNumber(): string | undefined {
    const pageNumber = this.$route.query.pageNumber

    if (!pageNumber || typeof pageNumber !== 'string') {
      // 未指定時、string型でない場合はundefinedを返す
      return undefined
    }

    return pageNumber
  }

  /**
   * 選択中の学年を取得
   *
   * @returns 学年コード
   */
  private get currentGrade(): string {
    return this.studentLearningProgressLayout.currentGrade()
  }

  /**
   * 選択中のコースを取得
   *
   * @returns コースコード(ID)
   */
  private get currentAcademyCourse(): string | undefined {
    // コース未選択(全て)の場合、undefinedを返す
    if (this.studentLearningProgressLayout.currentCourse() === this.OPTION_ALL.value) {
      return undefined
    }
    return this.studentLearningProgressLayout.currentCourse()
  }

  /**
   * 選択中のクラスを取得
   *
   * @returns クラスコード(ID)
   */
  private get currentAcademyClass(): string | undefined {
    // クラスが選択不可の場合、undefinedを返す
    if (this.studentLearningProgressLayout.currentClass() === this.OPTION_DISABLED.value) {
      return undefined
    }
    // クラス未選択(全て)の場合、undefinedを返す
    if (this.studentLearningProgressLayout.currentClass() === this.OPTION_ALL.value) {
      return undefined
    }

    return this.studentLearningProgressLayout.currentClass()
  }

  /**
   * 生徒検索の文字列
   *
   * @returns 検索文字列
   */
  private get currentSearchText(): string | undefined {
    // 検索欄が未入力の場合、undefinedを返す
    if (StringUtils.isBlank(this.studentLearningProgressLayout.textInputedSearch())) {
      return undefined
    }

    return this.studentLearningProgressLayout.textInputedSearch()
  }

  /**
   * 選択中の教科
   *
   * @returns 教科コード
   */
  public get currentSubject(): string {
    return this.studentLearningProgressLayout.currentSubject()
  }

  /**
   * 選択中の系統図
   *
   * @returns 系統図ID
   */
  public get currentSystemChart(): string | undefined {
    // 教材IDが未指定の場合、undefinedを返す
    if (StringUtils.isBlank(this.studentLearningProgressLayout.currentProgressUnit())) {
      return undefined
    }

    return this.studentLearningProgressLayout.currentProgressUnit()
  }

  /**
   * 通常学習タブクリック時の処理
   */
  private clickGeneralLearningTab(): void {
    this.$router.push({
      path: '/teacher/students/progress/general',
      query: {
        subjectCode: this.currentSubject,
        gradeCode: this.currentGrade,
        courseId: this.currentAcademyCourse,
        classId: this.currentAcademyClass,
        searchText: this.currentSearchText,
        pageNumber: String(this.currentPage),
      },
    })
  }

  /**
   * 初期処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()

    // コース一覧取得(プルダウン設定)
    await this.getAcademyCourses(this.queryCourseId)

    // クラス一覧取得(プルダウン設定)
    await this.getAcademyClasses(this.queryClassId)

    // 系統図一覧取得
    await this.getSystemCharts(this.querySystemChartId)

    // 検索文字列の初期値を設定
    this.setCurrentSearchText()

    // ページ番号設定
    this.currentPage = Number(this.queryPageNumber) || this.DEFAULT_PAGE_NUMBER

    // 生徒一覧取得
    await this.getStudents()

    // クエリが存在しない場合、クエリパラメータを更新
    if (Object.keys(this.$route.query).length === 0) {
      await this.replaceQueryParams()
    }

    this.$loading.complete()
  }

  /**
   * 検索文字列を設定
   */
  public setCurrentSearchText(): void {
    this.studentLearningProgressLayout.setCurrentSearchText(this.querySearchText || '')
  }

  /**
   * コース一覧取得(コースプルダウン設定)
   *
   * @param courseId コースID
   */
  private async getAcademyCourses(courseId?: string): Promise<void> {
    // コース一覧取得のパラメータ設定
    let options = { branchId: this.userBranch.branchId }

    // 選択中の学年があれば追加
    if (this.currentGrade) {
      Object.assign(options, { gradeCode: this.currentGrade })
    }

    // コースフィルターの選択肢セット
    const academy_courses = await AcademyCoursesApi.getCoursesHasStudents(options)

    // コースプルダウン設定
    this.filterAcademyCourseList = academy_courses.map((course) => {
      return {
        // コースIDをコードとして使用
        value: String(course.id),
        label: course.name,
      }
    })

    // コースフィルター先頭に「全て」の選択肢を追加
    this.filterAcademyCourseList.unshift(this.OPTION_ALL)

    // コースフィルターの初期選択値設定
    this.setCurrentCourseValue(courseId || this.OPTION_ALL.value)
  }

  /**
   * コース選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  private setCurrentCourseValue(value: string): void {
    // 指定コースの存在チェック
    const course = this.filterAcademyCourseList.find((course) => course.value === value)

    this.studentLearningProgressLayout.setCurrentCourseValue(course?.value || this.filterAcademyCourseList[0].value)
  }

  /**
   * クラス一覧取得(クラスプルダウン設定)
   *
   * @param classId クラスID
   */
  private async getAcademyClasses(classId?: string): Promise<void> {
    // disabledを外す
    this.isClassFilterDisabled = false

    // コースが未選択(全てのコース)の場合、固定値を設定し処理を終了(クラス選択はDisabledになる)
    if (!this.currentAcademyCourse) {
      this.filterAcademyClassList = [this.OPTION_DISABLED]
      this.setCurrentClassValue(this.OPTION_DISABLED.value)
      this.isClassFilterDisabled = true
      return
    }

    // クラス一覧取得のパラメータ設定
    let options = { academyCourseId: Number(this.currentAcademyCourse) }

    // 選択中の学年があれば追加
    if (this.currentGrade) {
      Object.assign(options, { gradeCode: this.currentGrade })
    }

    // クラス一覧取得
    const academyClasses = await AcademyClassesApi.getClassesHasStudents(options)

    // クラスプルダウン設定
    this.filterAcademyClassList = academyClasses.map((academyClass) => {
      return {
        value: String(academyClass.id),
        label: academyClass.name,
      }
    })

    // クラスプルダウンの先頭に「全て」の選択肢を追加
    this.filterAcademyClassList.unshift(this.OPTION_ALL)

    // クラスフィルターの初期選択値設定
    this.setCurrentClassValue(classId || this.OPTION_ALL.value)
  }

  /**
   * クラス選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  private setCurrentClassValue(value: string): void {
    // 指定クラスの存在チェック
    const academyClass = this.filterAcademyClassList.find((academyClass) => academyClass.value === value)

    this.studentLearningProgressLayout.setCurrentClassValue(academyClass?.value || this.filterAcademyClassList[0].value)
  }

  /**
   * 系統図一覧取得(プルダウン設定)
   *
   * @param systemChartId 系統図ID
   */
  private async getSystemCharts(systemChartId?: string): Promise<void> {
    // 系統図一覧取得
    const systemCharts = await SystemChartsApi.getSystemCharts(this.currentSubject)

    // 系統図情報がなかった場合、「選択不可」の選択肢を設定
    //   運用上発生する見込みは少ないが、予期せぬエラー回避のため記述
    if (systemCharts.length === 0) {
      this.filterSystemChartList = [this.OPTION_DISABLED]
      this.setCurrentSystemChartValue(this.OPTION_DISABLED.value)
      return
    }

    // 系統図フィルター
    this.filterSystemChartList = systemCharts.map((systemChart) => {
      return {
        value: String(systemChart.id),
        label: systemChart.name,
      }
    })

    // 系統図フィルターの初期選択値設定
    this.setCurrentSystemChartValue(systemChartId || this.filterSystemChartList[0].value)
  }

  /**
   * 系統図選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentSystemChartValue(value: string): void {
    // 指定系統図の存在チェック
    const systemChart = this.filterSystemChartList.find((systemChart) => systemChart.value === value)

    this.studentLearningProgressLayout.setCurrentProgressUnitValue(
      systemChart?.value || this.filterSystemChartList[0].value
    )
  }

  /**
   * 生徒一覧取得
   */
  private async getStudents(): Promise<void> {
    // 生徒一覧取得のパラメータ設定
    let options = this.getCurrentOptions()

    // 系統図指定がない、もしくは選択不可の場合
    //   運用上発生する見込みは少ないが、予期せぬエラー回避のため記述
    if (!this.currentSystemChart || this.currentSystemChart === this.OPTION_DISABLED.value) {
      this.studentList = []

      // 生徒数設定
      this.studentsNumber = this.studentList.length

      // ページング設定
      const divideNumber = Math.floor(this.studentsNumber / this.MAX_PER_PAGE)
      this.pageLength = this.studentsNumber % this.MAX_PER_PAGE ? divideNumber + 1 : divideNumber

      // 処理終了
      return
    }

    // 生徒進捗一覧取得
    const responseStudents = await UserStudentsApi.getStudentSystemChartProgresses(
      Number(this.currentSystemChart),
      this.currentPage,
      this.MAX_PER_PAGE,
      options
    )

    // 生徒数設定
    this.studentsNumber = responseStudents.totalCount

    // ページング設定
    const divideNumber = Math.floor(this.studentsNumber / this.MAX_PER_PAGE)
    this.pageLength = this.studentsNumber % this.MAX_PER_PAGE ? divideNumber + 1 : divideNumber

    // 生徒一覧テーブルデータ設定
    this.studentList = responseStudents.students.map((student) => {
      // 進捗率を取得
      //   存在する場合は100倍しパーセント表示対応、存在しない場合は0を代入
      const progress = student.progressRate ? student.progressRate * 100 : 0

      return {
        userId: student.userId,
        avatarIconCode: student.avatarIconCode,
        nickname: student.nickname,
        loginId: student.loginId,
        identifyTag: student.identifyTag,
        gradeCode: student.gradeCode,
        branchName: student.branchName,
        courseName: student.courseName,
        className: student.className,
        // 小数点第一位まで表示
        progressRate: Math.round(progress * 10) / 10,
      }
    })
  }

  /**
   * 現在の絞り込み条件を取得
   *
   * @returns 絞り込み条件
   */
  private getCurrentOptions(): {
    /** 学年コード */
    gradeCode?: string
    /** コースID */
    courseId?: number
    /** クラスID */
    classId?: number
    /** 検索文字列 */
    searchText?: string
  } {
    let options = {}

    // 学年指定がある場合追加
    if (this.currentGrade) {
      Object.assign(options, { gradeCode: this.currentGrade })
    }

    // コース指定がある場合追加
    if (this.currentAcademyCourse) {
      Object.assign(options, { courseId: Number(this.currentAcademyCourse) })
    }

    // クラス指定がある場合追加
    if (this.currentAcademyClass) {
      Object.assign(options, { classId: Number(this.currentAcademyClass) })
    }

    // 名前検索文字列がある場合追加
    if (this.currentSearchText) {
      Object.assign(options, { searchText: this.currentSearchText })
    }

    return options
  }

  /**
   * クエリパラメータの更新
   */
  private async replaceQueryParams(): Promise<void> {
    await this.$router.replace({
      query: {
        subjectCode: this.currentSubject,
        gradeCode: this.currentGrade,
        courseId: this.currentAcademyCourse,
        classId: this.currentAcademyClass,
        searchText: this.currentSearchText,
        systemChartId: this.currentSystemChart,
        pageNumber: String(this.currentPage),
      },
    })
  }

  /**
   * 学年フィルター変更時のイベント
   */
  private async changeGradeFilter(): Promise<void> {
    this.$loading.start()

    // コース一覧取得(コースプルダウン設定)
    await this.getAcademyCourses()

    // クラス一覧取得(クラスプルダウン設定)
    await this.getAcademyClasses()

    // ページ指定をリセット(生徒一覧取得件数が現在の指定ページ数に届かないケースがあるため)
    this.currentPage = this.DEFAULT_PAGE_NUMBER

    // 生徒一覧取得
    await this.getStudents()

    // クエリパラメータの更新
    await this.replaceQueryParams()

    this.$loading.complete()
  }

  /**
   * コースフィルター変更時のイベント
   */
  private async changeCourseFilter(): Promise<void> {
    this.$loading.start()

    // クラス一覧取得(クラスプルダウン設定)
    await this.getAcademyClasses()

    // ページ指定をリセット(生徒一覧取得件数が現在の指定ページ数に届かないケースがあるため)
    this.currentPage = this.DEFAULT_PAGE_NUMBER

    // 生徒一覧取得
    await this.getStudents()

    // クエリパラメータの更新
    await this.replaceQueryParams()

    this.$loading.complete()
  }

  /**
   * クラスフィルター変更時のイベント
   */
  private async changeClassFilter(): Promise<void> {
    this.$loading.start()

    // ページ指定をリセット(生徒一覧取得件数が現在の指定ページ数に届かないケースがあるため)
    this.currentPage = this.DEFAULT_PAGE_NUMBER

    // 生徒一覧取得
    await this.getStudents()

    // クエリパラメータの更新
    await this.replaceQueryParams()

    this.$loading.complete()
  }

  /**
   * 生徒検索
   */
  private async searchStudent(): Promise<void> {
    this.$loading.start()

    // ページ指定をリセット(生徒一覧取得件数が現在の指定ページ数に届かないケースがあるため)
    this.currentPage = this.DEFAULT_PAGE_NUMBER

    // 生徒一覧取得
    await this.getStudents()

    // 検索値に変更があった場合のみ更新する(Vue Routerのエラー回避のため)
    if (this.currentSearchText !== this.querySearchText) {
      // クエリパラメータの更新
      await this.replaceQueryParams()
    }

    this.$loading.complete()
  }

  /**
   * 教科フィルター変更時のイベント
   */
  private async changeSubjectFilter(): Promise<void> {
    this.$loading.start()

    // 系統図一覧再取得
    await this.getSystemCharts()

    // 生徒一覧再取得
    await this.getStudents()

    // クエリパラメータの更新
    await this.replaceQueryParams()

    this.$loading.complete()
  }

  /**
   * 系統図フィルター変更時のイベント
   */
  private async changeSystemChartFilter(): Promise<void> {
    this.$loading.start()

    // 生徒一覧再取得
    await this.getStudents()

    // クエリパラメータの更新
    await this.replaceQueryParams()

    this.$loading.complete()
  }

  /**
   * 詳細ボタン押下時の処理
   *
   * @param userId 対象生徒ユーザID
   */
  private async onClickStudentListDetailButton(userId: number): Promise<void> {
    // 生徒個人の進捗詳細画面に遷移
    await this.$router.push({
      path: `/teacher/students/${userId}/map`,
      query: this.$prevPage.generateQuery(),
    })
  }

  /**
   * ページリンク押下時の処理
   *
   * @param value ページ番号
   */
  private async changePage(value: number): Promise<void> {
    this.$loading.start()

    // 現在のページを更新
    this.currentPage = value

    // 生徒一覧取得
    await this.getStudents()

    // クエリパラメータの更新
    await this.replaceQueryParams()

    this.$loading.complete()
  }
}
